import { FileText, Send } from "lucide-react";
import { Fragment, useState, type FC } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import { formatDate, formatDeadline } from "../../companies/utils";
import { PortalAPI } from "../api/portalApi";
import {
  refreshPortal,
  useEmployeeDocuments,
  useEmployeeMessages
} from "../queries";
import type { PortalEmployee, PortalMe } from "../types";

const Documents: FC<{ employee: PortalEmployee }> = ({ employee }) => {
  const { data, isLoading } = useEmployeeDocuments(
    employee.individual_id,
    true
  );
  const open = async (id: string) => {
    try {
      window.open(
        await PortalAPI.documentUrl(employee.individual_id, id),
        "_blank",
        "noopener"
      );
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Couldn't open the document"
      );
    }
  };
  if (isLoading) return <Skeleton className="h-16" />;
  if (!data?.length)
    return (
      <span className="text-sm text-muted-foreground">
        No notices or forms yet. USCIS receipts and approvals show here once the
        firm files them.
      </span>
    );
  return (
    <div className="flex flex-col">
      {data.map((d) => (
        <button
          key={d.id}
          type="button"
          onClick={() => void open(d.id)}
          className="flex items-center gap-2.5 rounded-md px-2 py-1.5 text-left hover:bg-muted"
        >
          <FileText className="size-4 shrink-0 text-muted-foreground" />
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-base">{d.title}</span>
            <span className="text-sm text-muted-foreground">
              {d.kind === "notice" ? "USCIS notice" : "Form"}
              {d.created_at ? ` · ${formatDate(d.created_at)}` : ""}
            </span>
          </span>
        </button>
      ))}
    </div>
  );
};

const Messages: FC<{ employee: PortalEmployee; me: PortalMe }> = ({
  employee,
  me
}) => {
  const { data } = useEmployeeMessages(employee.individual_id, true);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const send = async () => {
    setSending(true);
    try {
      await PortalAPI.postMessage(employee.individual_id, body.trim());
      setBody("");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Couldn't send the message"
      );
    } finally {
      setSending(false);
      void refreshPortal();
    }
  };
  return (
    <div className="flex flex-col gap-2">
      {(data ?? []).map((m) => (
        <div
          key={m.id}
          className={cn(
            "rounded-md px-2.5 py-1.5 text-sm leading-4.5",
            m.author.id === me.contact.id ? "bg-muted" : "border border-border"
          )}
        >
          <span className="flex gap-2 text-sm text-muted-foreground">
            <span className="font-medium text-foreground">
              {m.author.id === me.contact.id ? "You" : m.author.name}
            </span>
            {m.created_at ? formatDate(m.created_at) : ""}
          </span>
          <span className="whitespace-pre-wrap">{m.body}</span>
        </div>
      ))}
      <Textarea
        rows={3}
        value={body}
        disabled={me.preview}
        aria-label={`Message ${me.firm.name}`}
        placeholder={
          me.preview
            ? "Messages can't be sent from a preview."
            : `Message ${me.firm.name} about ${
                employee.name.split(" ")[0]
              }'s case`
        }
        onChange={(e) => setBody(e.currentTarget.value)}
        className="text-base"
      />
      <span className="flex justify-end">
        <Button
          variant="primary"
          size="sm"
          disabled={me.preview || !body.trim() || sending}
          onClick={() => void send()}
        >
          <Send /> Send
        </Button>
      </span>
    </div>
  );
};

export const EmployeeSheet: FC<{
  employee: PortalEmployee | null;
  me: PortalMe;
  onClose: () => void;
}> = ({ employee, me, onClose }) => (
  <Sheet open={employee !== null} onOpenChange={(o) => !o && onClose()}>
    <SheetContent className="flex w-105 flex-col gap-0 overflow-y-auto p-0 sm:max-w-105">
      {employee ? (
        <>
          <SheetHeader className="border-b border-border">
            <SheetTitle>{employee.name}</SheetTitle>
            <SheetDescription>
              {employee.case_label} · {employee.status}
            </SheetDescription>
          </SheetHeader>
          <dl className="grid grid-cols-[84px_minmax(0,1fr)] gap-x-3 gap-y-1.5 border-b border-border px-4 py-3 text-base">
            {(
              [
                ["Next date", formatDeadline(employee.next_date)],
                ["Employer", employee.entity],
                ["Role", employee.job],
                ["Worksite", employee.worksite]
              ] as const
            )
              .filter(([, value]) => value)
              .map(([label, value]) => (
                <Fragment key={label}>
                  <dt className="text-muted-foreground">{label}</dt>
                  <dd className="truncate">{value}</dd>
                </Fragment>
              ))}
          </dl>
          {me.features.docs ? (
            <div className="flex flex-col gap-2 border-b border-border px-4 py-3">
              <span className="text-xs font-medium text-muted-foreground">
                Notices and forms
              </span>
              <Documents employee={employee} />
            </div>
          ) : null}
          {me.features.messages ? (
            <div className="flex flex-col gap-2 px-4 py-3">
              <span className="text-xs font-medium text-muted-foreground">
                Messages with {me.firm.name}
              </span>
              <Messages employee={employee} me={me} />
            </div>
          ) : null}
        </>
      ) : null}
    </SheetContent>
  </Sheet>
);
